import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FilePreviewProvider, useFilePreview } from "@/context/FilePreviewContext";
import FilePreviewDrawer from "@/components/FilePreviewDrawer";
import { STATUS_META } from "@/pages/Compliance";
import {
  Ambulance, ArrowLeft, ArrowRight, Camera, CheckCircle2, Circle, Clock3, Upload,
} from "lucide-react";

// STATUS_META (Compliance.jsx) covers valid/expiring/expired/missing. The wizard also uses
// "pending" for an agreement that has not been signed yet, so it gets its own entry here
// rather than adding a fifth status to a shared component that was not built for it.
const WIZARD_STATUS_META = {
  ...STATUS_META,
  pending: { label: "Not signed", Icon: Clock3, cls: "text-slate-600 bg-slate-50 border-slate-200" },
};

const ROUTE_COPY = {
  license: {
    blurb: "Add a photo of your driver's license, front and back. We read it automatically and use it to fill in the rest of your paperwork.",
    button: "Add my driver's license",
  },
  application: {
    blurb: "Fill out your job application: your personal details, availability, and work history.",
    button: "Fill out job application",
  },
  agreements: {
    blurb: "Read and sign the agreements your company requires before you can start driving.",
    button: "Review and sign agreements",
  },
  w9: {
    blurb: "Fill out your IRS Form W-9 for tax purposes, or upload a signed copy if you already have one.",
    button: "Fill out W-9",
  },
};

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif";

export default function DriverOnboarding() {
  return (
    <FilePreviewProvider>
      <DriverOnboardingInner />
    </FilePreviewProvider>
  );
}

function DriverOnboardingInner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openPreview } = useFilePreview();

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [busyType, setBusyType] = useState(null);

  const load = useCallback((landOnFirstIncomplete) => {
    setLoading(true);
    return api.get("/driver/onboarding")
      .then((r) => {
        setState(r.data);
        if (landOnFirstIncomplete) {
          setIndex(Math.min(r.data.first_incomplete, r.data.steps.length - 1));
        }
        return r.data;
      })
      .catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail));
        return null;
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(true); }, [load]);

  const upload = async (docType, file) => {
    if (!file || !user?.driver_id) return;
    setBusyType(docType);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", file);
      await api.post(`/drivers/${user.driver_id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      await load(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyType(null);
    }
  };

  const viewItem = (item) => {
    if (!item.doc_id) return;
    openPreview({ kind: "document", id: item.doc_id, meta: { label: item.label, driver_id: user?.driver_id, canEdit: false } });
  };

  if (loading && !state) {
    return (
      <Shell user={user} navigate={navigate}>
        <div className="text-sm text-slate-500 p-4">Loading…</div>
      </Shell>
    );
  }
  if (!state) return <Shell user={user} navigate={navigate}>{null}</Shell>;

  const steps = state.steps;
  const total = state.total;
  const allDone = state.first_incomplete >= steps.length;
  const requiredDone = steps.filter((s) => !s.optional).every((s) => s.done);
  const pct = total > 0 ? Math.round((state.completed / total) * 100) : 0;
  const step = steps[Math.min(index, steps.length - 1)];

  const goToRoute = () => {
    if (step.route) navigate(`${step.route}?return=/driver/onboarding`);
  };

  const next = async () => {
    const data = await load(false);
    const count = data ? data.steps.length : steps.length;
    setIndex((i) => Math.min(i + 1, count - 1));
  };

  const back = () => setIndex((i) => Math.max(i - 1, 0));

  return (
    <Shell user={user} navigate={navigate}>
      {allDone ? (
        <CompletionScreen navigate={navigate} />
      ) : (
        <>
          {requiredDone && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 mb-4 text-sm text-emerald-800" data-testid="onboarding-required-done-banner">
              Every required step is complete. The section below is optional, add to it whenever you have more to file.
              <button onClick={() => navigate("/driver")} className="min-h-[40px] mt-2 w-full text-sm bg-[#0A192F] hover:bg-[#112240] text-white rounded-md" data-testid="onboarding-go-to-rides-early">
                Go to my rides
              </button>
            </div>
          )}

          <div className="mb-4">
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-display font-semibold text-slate-900" data-testid="onboarding-step-label">
                Step {index + 1} of {total}
              </span>
              <span className="text-slate-500 text-xs" data-testid="onboarding-progress-label">
                {state.completed} of {total} complete &middot; {pct}%
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2">
              <div className="h-full bg-[#005E82]" style={{ width: `${pct}%` }} />
            </div>
          </div>

          <div className="flex gap-1.5 overflow-x-auto pb-1 mb-4" data-testid="onboarding-step-jump">
            {steps.map((s, i) => (
              <button
                key={s.key}
                onClick={() => setIndex(i)}
                className={`flex-shrink-0 min-h-[36px] px-2.5 rounded-full border text-[11px] font-medium inline-flex items-center gap-1
                  ${i === index ? "border-[#0A192F] bg-[#0A192F] text-white" : s.done ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600"}`}
                data-testid={`onboarding-jump-${s.key}`}
              >
                {s.done ? <CheckCircle2 size={12} /> : <Circle size={12} />}
                {s.title}{s.optional ? " (optional)" : ""}
              </button>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={`onboarding-step-${step.key}`}>
            <div className="font-display font-semibold text-slate-900 text-lg">
              {step.title}{step.optional ? <span className="ml-2 text-xs font-normal text-slate-400">optional</span> : null}
            </div>

            {step.route ? (
              <RouteStepBody step={step} onGo={goToRoute} onView={viewItem} />
            ) : (
              <UploadStepBody step={step} onUpload={upload} onView={viewItem} busyType={busyType} />
            )}

            <div className="flex items-center gap-2 mt-5 pt-4 border-t border-slate-100">
              <button
                onClick={back}
                disabled={index === 0}
                className="min-h-[44px] px-4 text-sm rounded-md border border-slate-200 disabled:opacity-40 inline-flex items-center gap-1.5"
                data-testid="onboarding-back-btn"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <button
                onClick={next}
                className="min-h-[44px] flex-1 text-sm rounded-md bg-[#0A192F] hover:bg-[#112240] text-white inline-flex items-center justify-center gap-1.5"
                data-testid="onboarding-next-btn"
              >
                Next <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </>
      )}
    </Shell>
  );
}

function RouteStepBody({ step, onGo, onView }) {
  const copy = ROUTE_COPY[step.key] || { blurb: "", button: "Continue" };
  return (
    <div className="mt-3">
      <div className="text-sm text-slate-600">{copy.blurb}</div>
      {step.items.length > 0 && (
        <div className="mt-3 space-y-2">
          {step.items.map((it) => {
            const meta = WIZARD_STATUS_META[it.status] || WIZARD_STATUS_META.missing;
            const Icon = meta.Icon;
            return (
              <div key={it.agreement_key || it.doc_type} className="flex items-center justify-between gap-2 border border-slate-100 rounded-md px-3 py-2">
                <span className="text-sm text-slate-800">{it.label}</span>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-medium rounded-full px-2 py-0.5 border ${meta.cls}`}>
                    <Icon size={10} /> {meta.label}
                  </span>
                  {it.doc_id && (
                    <button onClick={() => onView(it)} className="text-[#005E82] hover:underline text-xs" data-testid={`onboarding-view-${it.doc_type}`}>
                      View
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <button
        onClick={onGo}
        className="mt-4 w-full min-h-[44px] text-sm rounded-md border border-[#005E82] text-[#005E82] hover:bg-slate-50"
        data-testid={`onboarding-go-${step.key}`}
      >
        {copy.button}
      </button>
    </div>
  );
}

function UploadStepBody({ step, onUpload, onView, busyType }) {
  return (
    <div className="mt-3 space-y-3">
      {step.optional && (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2.5">
          None of these are required to finish onboarding. Add them here if you have them, or come back to this later.
        </div>
      )}
      {step.items.length === 0 && (
        <div className="text-sm text-slate-500">Nothing to file here right now.</div>
      )}
      {step.items.map((it) => (
        <UploadTile key={it.doc_type} item={it} onUpload={onUpload} onView={onView} busy={busyType === it.doc_type} />
      ))}
    </div>
  );
}

function UploadTile({ item, onUpload, onView, busy }) {
  const meta = WIZARD_STATUS_META[item.status] || WIZARD_STATUS_META.missing;
  const Icon = meta.Icon;
  const fileInputId = `onboarding-file-${item.doc_type}`;
  const cameraInputId = `onboarding-camera-${item.doc_type}`;

  return (
    <div className="border border-slate-200 rounded-md p-3" data-testid={`onboarding-tile-${item.doc_type}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-slate-900">{item.label}</div>
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium rounded-full px-2 py-0.5 border ${meta.cls}`}>
          <Icon size={10} /> {meta.label}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-2.5 flex-wrap">
        {item.doc_id && (
          <button onClick={() => onView(item)} className="text-[#005E82] hover:underline text-xs min-h-[36px]" data-testid={`onboarding-view-${item.doc_type}`}>
            View
          </button>
        )}
        <label htmlFor={fileInputId} className="btn-primary text-[11px] px-3 min-h-[36px] inline-flex items-center gap-1.5 cursor-pointer" data-testid={`onboarding-upload-${item.doc_type}`}>
          <Upload size={12} /> {busy ? "Uploading…" : item.doc_id ? "Replace" : "Upload"}
        </label>
        <input
          id={fileInputId}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(item.doc_type, f); }}
        />
        {/* Second, camera-shortcut input. The plain file input above already offers the photo
            library on iOS; this one adds a quick "take a photo now" path with capture, it is
            never the only way to add a file. */}
        <label htmlFor={cameraInputId} className="text-slate-500 text-xs min-h-[36px] px-2 inline-flex items-center gap-1 border border-slate-200 rounded-md cursor-pointer" data-testid={`onboarding-camera-${item.doc_type}`}>
          <Camera size={12} /> Camera
        </label>
        <input
          id={cameraInputId}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(item.doc_type, f); }}
        />
      </div>
    </div>
  );
}

function CompletionScreen({ navigate }) {
  return (
    <div className="bg-white border border-emerald-200 rounded-md p-8 text-center" data-testid="onboarding-complete">
      <CheckCircle2 size={40} className="text-emerald-600 mx-auto mb-3" />
      <div className="font-display font-semibold text-slate-900 text-xl">You're all set</div>
      <div className="text-sm text-slate-600 mt-1.5">Every step of your onboarding is complete.</div>
      <button
        onClick={() => navigate("/driver")}
        className="mt-5 min-h-[44px] w-full max-w-xs mx-auto flex items-center justify-center gap-2 bg-[#0A192F] hover:bg-[#112240] text-white text-sm rounded-md"
        data-testid="onboarding-go-to-rides"
      >
        Go to my rides
      </button>
    </div>
  );
}

function Shell({ user, navigate, children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#005E82] flex items-center justify-center"><Ambulance size={18} /></div>
            <div>
              <div className="font-display font-semibold text-sm">Getting started</div>
              <div className="text-[11px] text-slate-400 truncate max-w-[180px]">{user?.name}</div>
            </div>
          </div>
          <button onClick={() => navigate("/driver")} className="min-h-[44px] text-slate-300 text-xs flex items-center gap-1.5 hover:text-white" data-testid="onboarding-back">
            <ArrowLeft size={14} /> Back to rides
          </button>
        </div>
      </header>
      <div className="max-w-lg mx-auto px-4 pt-5 pb-24" data-testid="driver-onboarding">
        {children}
      </div>
      <FilePreviewDrawer />
    </div>
  );
}
